'use client'

import { useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'

import { useMounted } from '@/hooks/use-unmounted'
import { getCookie, setCookie } from '@/lib/utils/cookie-client'

/**
 * Lightweight cookie-consent banner.
 *
 * Prescia AI currently uses essential cookies only (see /cookies), so this
 * banner records the user's choice and links the policy rather than gating a
 * tracker that does not yet exist. When non-essential cookies are introduced,
 * gate them on `getCookie(CONSENT_COOKIE) === 'accepted'`.
 *
 * Reuses the secure cookie helpers in `src/lib/utils/cookie-client.ts`
 * (SameSite=Lax, Secure on HTTPS).
 */

const CONSENT_COOKIE = 'prescia-cookie-consent'
const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365

type Consent = 'accepted' | 'rejected'

export function CookieConsentBanner() {
  const pathname = usePathname()
  const mounted = useMounted()
  // After the user clicks, hide immediately without waiting on a cookie re-read.
  const [dismissed, setDismissed] = useState(false)

  const existing = mounted ? getCookie(CONSENT_COOKIE) : null
  const hasChoice = existing === 'accepted' || existing === 'rejected'
  const visible = mounted && !hasChoice && !dismissed

  function record(choice: Consent) {
    setCookie(CONSENT_COOKIE, choice, {
      maxAge: ONE_YEAR_SECONDS,
      sameSite: 'Lax',
    })
    setDismissed(true)
  }

  // The Word add-in task pane (src/app/addin/**) is a ~320px surface with
  // its own minimal chrome — there's no room for, and no need for, the
  // main app's cookie banner there.
  if (pathname?.startsWith('/addin')) return null

  if (!visible) return null

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      aria-live="polite"
      className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-2xl"
    >
      <div className="corner-squircle flex flex-col gap-3 rounded-xl border border-border bg-card px-5 py-4 shadow-lg sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm leading-relaxed text-muted-foreground">
          We use essential cookies to run Prescia AI and keep you signed in. See
          our{' '}
          <Link href="/cookies" className="text-brand hover:underline">
            Cookie Policy
          </Link>
          .
        </p>
        <div className="flex shrink-0 gap-2">
          <button
            type="button"
            onClick={() => record('rejected')}
            className="corner-squircle rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-muted"
          >
            Reject non-essential
          </button>
          <button
            type="button"
            onClick={() => record('accepted')}
            className="corner-squircle rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-brand-foreground transition-opacity hover:opacity-90"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  )
}
